import React, { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/app';
import 'firebase/remote-config';
import camelcaseKeys from 'camelcase-keys';
import { getRemoteConfigJSON } from './utils';

const FirebaseContext = createContext();

function FirebaseConfigRoot({ children }) {
  const remoteConfig = firebase.remoteConfig();
  const [config, setConfig] = useState({});
  const [ready, setReady] = useState(false);
  const value = useMemo(() => ({ config, ready }), [config, ready]);

  useEffect(() => {
    let unmounted = false;

    (async () => {
      try {
        await remoteConfig.fetchAndActivate();
      } catch {
        // Last activated values / defaults still apply.
      }
      if (unmounted) return;
      setConfig(camelcaseKeys(remoteConfig.getAll()));
      setReady(true);
    })().catch(() => {});

    return () => {
      unmounted = true;
    };
  }, [remoteConfig]);

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
}

function FirebaseConfigProvider({ children }) {
  const parent = React.useContext(FirebaseContext);
  if (parent) {
    return children;
  }
  return <FirebaseConfigRoot>{children}</FirebaseConfigRoot>;
}

FirebaseConfigProvider.propTypes = { children: PropTypes.node.isRequired };
FirebaseConfigRoot.propTypes = { children: PropTypes.node.isRequired };

export const errorMessage =
  'useFeatureSwitch hook must be used inside FirebaseConfigProvider.';

export const remoteConfigErrorMessage =
  'useRemoteConfig hook need a default value.';

const featureSwitchSource = {
  remote: 'remote',
  default: 'default'
};

/**
 * Get the feature switch value from the list of remote config or default config
 * @param {*} listOfFeatureSwitches list of feature switches (remote config or default config)
 *                                  - remote config: obtained from fetching values on firebase
 *                                  - default config: defined in application/target
 * @param {*} featureSwitchName name of the feature switch we want to get the value of
 * @param {*} source source of the feature switch value
 * @returns The value of FS, if it is a boolean, it returns a true or false, otherwise it returns the value configured
 */
function getFeatureSwitchValue({
  listOfFeatureSwitches,
  featureSwitchName,
  source = featureSwitchSource.remote
}) {
  const booleans = { true: true, false: false };
  const value =
    source === featureSwitchSource.remote
      ? listOfFeatureSwitches[featureSwitchName]._value
      : listOfFeatureSwitches[featureSwitchName];

  if (value in booleans) {
    return booleans[value];
  }

  return value;
}

/**
 * True after the first fetchAndActivate (or its failure).
 * Tests that omit `ready` on the context keep the previous behavior (ready).
 */
function useRemoteConfigReady() {
  const fsContext = React.useContext(FirebaseContext);

  if (!fsContext) throw new Error(errorMessage);

  return fsContext.ready !== false;
}

/**
 * Reads a boolean Remote Config key.
 * `null` means the SDK is not usable yet (do not treat as "flag off").
 */
function readRemoteConfigBoolean(flagKey) {
  try {
    return firebase
      .remoteConfig()
      .getValue(flagKey)
      .asBoolean();
  } catch {
    return null;
  }
}

function useFeatureSwitch(featureSwitch) {
  const fsContext = React.useContext(FirebaseContext);

  if (!fsContext) throw new Error(errorMessage);

  if (!('config' in fsContext)) return false;

  const { defaultConfig = {} } = firebase.remoteConfig() || {};

  const fs = fsContext.config;

  if (featureSwitch in fs) {
    return getFeatureSwitchValue({
      listOfFeatureSwitches: fs,
      featureSwitchName: featureSwitch
    });
  }

  if (featureSwitch in defaultConfig) {
    return getFeatureSwitchValue({
      listOfFeatureSwitches: defaultConfig,
      featureSwitchName: featureSwitch,
      source: featureSwitchSource.default
    });
  }

  return false;
}

function useRemoteConfig(remoteConfigFlag, defaultValue) {
  const fsContext = React.useContext(FirebaseContext);

  if (!fsContext) throw new Error(errorMessage);

  if (!defaultValue) throw new Error(remoteConfigErrorMessage);
  if (!('config' in fsContext)) return defaultValue;

  const configs = fsContext.config;
  if (remoteConfigFlag in configs) {
    return getRemoteConfigJSON(configs[remoteConfigFlag]._value, defaultValue);
  }

  return defaultValue;
}

export default FirebaseConfigProvider;
export {
  useFeatureSwitch,
  useRemoteConfigReady,
  readRemoteConfigBoolean,
  FirebaseContext,
  useRemoteConfig
};
export {
  getSplittedValuesFromFS,
  isFeatureSwitchEnabledForValue
} from './utils';
